import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../lib/cart";
import { Check, X } from "lucide-react";

export default function SuccessPage() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const order_id = params.get("order_id");
  const method = params.get("method");
  const { clear } = useCart();
  const [status, setStatus] = useState(method ? "paid" : "checking");
  const [attempts, setAttempts] = useState(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!session_id) return;
    let active = true;
    const poll = async (n) => {
      if (!active || n >= 8) {
        if (n >= 8) setStatus("timeout");
        return;
      }
      try {
        const res = await api.get(`/checkout/status/${session_id}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) { cleared.current = true; clear(); }
          return;
        }
        if (res.data.status === "expired") { setStatus("expired"); return; }
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), 2000);
      } catch {
        setTimeout(() => poll(n + 1), 2000);
      }
    };
    poll(0);
    return () => { active = false; };
  }, [session_id]); // eslint-disable-line

  useEffect(() => {
    if (method && !cleared.current) { cleared.current = true; clear(); }
  }, [method]); // eslint-disable-line

  return (
    <div data-testid="success-page" className="max-w-[700px] mx-auto px-6 py-24 text-center">
      {status === "checking" && (
        <>
          <div className="font-serif text-3xl">Confirming your payment&hellip;</div>
          <p className="text-brand-muted mt-3 text-sm">Attempt {attempts + 1} of 8</p>
        </>
      )}
      {status === "paid" && (
        <>
          <div className="w-16 h-16 rounded-full border-2 border-brand-accent flex items-center justify-center mx-auto text-brand-accent">
            <Check size={28} />
          </div>
          <div className="overline mt-8">Order placed</div>
          <h1 className="font-serif text-4xl md:text-5xl mt-3" data-testid="success-heading">Thank you.</h1>
          <p className="text-brand-muted mt-4 max-w-md mx-auto">
            We've received your order and will be in touch shortly with shipping details.
            {method === "cod" && " Pay when your package arrives."}
          </p>
          <Link to="/shop" data-testid="success-shop-btn" className="btn-primary mt-10 inline-block">Keep shopping</Link>
        </>
      )}
      {(status === "timeout" || status === "expired") && (
        <>
          <div className="w-16 h-16 rounded-full border-2 border-brand-accent flex items-center justify-center mx-auto text-brand-accent">
            <X size={28} />
          </div>
          <h1 className="font-serif text-4xl mt-6">Couldn&apos;t confirm payment</h1>
          <p className="text-brand-muted mt-4">Please check your email or contact us for help.</p>
          <Link to="/shop" className="btn-outline mt-10 inline-block">Back to shop</Link>
        </>
      )}
    </div>
  );
}
